import { useState } from "react";
import {
  Button,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifyError } from "@marekl/adminer/api";
import {
  useCreateFloor,
  useDeleteFloor,
  useUpdateFloor,
  type Floor,
} from "../../../lib/queries/rules";
import { ComponentCard } from "./ComponentCard";
import { reorderRows } from "./reorder";
import tableStyles from "./componentTables.module.css";
import type { ID } from "../../../lib/id";

type ModalState = { kind: "closed" } | { kind: "floor"; floor?: Floor };

interface FloorForm {
  name: string;
  width: string;
  length: string;
  description: string;
}

const emptyForm: FloorForm = { name: "", width: "", length: "", description: "" };

const numberOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return Number(trimmed);
};

/** How a surface's dimensions read in the table. */
function sizeLabel(floor: Floor): string {
  if (floor.width_m == null || floor.length_m == null) return "—";
  return `${floor.width_m} × ${floor.length_m} m`;
}

/**
 * The competition surfaces of one rule set.
 *
 * A flat list rather than a table-of-tables: a discipline is performed
 * on one surface, so unlike the age or formation axes there is nothing
 * to group. It exists as rows at all because the same surface is shared
 * by most disciplines — as free text on each of them, one 12 × 12 m
 * became several spellings and no start list could group by it.
 */
export function FloorsTab({
  ruleSetId,
  readOnly,
  floors,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  floors: Floor[];
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [form, setForm] = useState<FloorForm>(emptyForm);

  const create = useCreateFloor(ruleSetId);
  const update = useUpdateFloor();
  const remove = useDeleteFloor();

  const close = () => {
    setModal({ kind: "closed" });
    create.reset();
    update.reset();
  };

  const open = (floor?: Floor) => {
    setForm(
      floor
        ? {
            name: floor.name,
            width: floor.width_m == null ? "" : String(floor.width_m),
            length: floor.length_m == null ? "" : String(floor.length_m),
            description: floor.description,
          }
        : emptyForm,
    );
    create.reset();
    update.reset();
    setModal({ kind: "floor", floor });
  };

  const submit = () => {
    if (modal.kind !== "floor") return;
    const payload = {
      name: form.name.trim(),
      width_m: numberOrNull(form.width),
      length_m: numberOrNull(form.length),
      description: form.description.trim(),
      sort_order: modal.floor?.sort_order ?? floors.length,
    };
    if (modal.floor) {
      update.mutate({ id: modal.floor.id, payload }, { onSuccess: close });
    } else {
      create.mutate(payload, { onSuccess: close });
    }
  };

  const move = (index: number, direction: -1 | 1) => {
    for (const { row, sort_order } of reorderRows(floors, index, direction)) {
      update.mutate({
        id: row.id,
        payload: {
          name: row.name,
          width_m: row.width_m ?? null,
          length_m: row.length_m ?? null,
          description: row.description,
          sort_order,
        },
      }, { onError: (err) => notifyError(err) });
    }
  };

  const width = numberOrNull(form.width);
  const length = numberOrNull(form.length);
  // The backend takes both dimensions or neither — half a rectangle is
  // not a size — so say it here rather than after the round trip.
  const halfSize = (width === null) !== (length === null);
  const badNumber = [width, length].some((v) => v !== null && (!Number.isInteger(v) || v <= 0));
  const invalid = !form.name.trim() || halfSize || badNumber;

  return (
    <>
      <Page.SectionHeader
        title="Soutěžní plocha"
        meta={
          readOnly ? undefined : (
            <Button variant="primary" onClick={() => open()}>
              Nová velikost
            </Button>
          )
        }
      />
      <Text variant="muted">
        Soutěží se na jedné ploše, mění se jen její velikost. Disciplína si velikost
        vybírá ze seznamu, takže přejmenování se propíše všude; rozměry jsou nepovinné,
        protože „celá plocha“ žádný pevný nemá.
      </Text>

      <ComponentCard<Floor>
        title="Velikosti plochy"
        subtitle={<Text variant="muted">{floors.length}</Text>}
        readOnly={readOnly}
        rows={floors}
        emptyTitle="Žádné velikosti"
        emptyDescription="Přidejte velikost, například 12 × 12 m nebo celou plochu haly."
        addLabel="Přidat velikost"
        columns={[
          {
            key: "name",
            header: "Velikost",
            tdClass: tableStyles.nameOnly,
            render: (f) => (
              <>
                <div>{f.name}</div>
                {f.description && <Text variant="muted">{f.description}</Text>}
              </>
            ),
          },
          {
            key: "size",
            header: "Rozměr",
            numeric: true,
            tdClass: tableStyles.value,
            render: (f) => <Text variant="mono">{sizeLabel(f)}</Text>,
          },
        ]}
        onAddRow={() => open()}
        onEditRow={(floor) => open(floor)}
        onDeleteRow={(floor) =>
          confirm({
            title: "Smazat velikost",
            description: (
              <>
                Opravdu smazat velikost <strong>„{floor.name}“</strong>? Velikost, kterou
                používá disciplína nebo kombinace, smazat nelze.
              </>
            ),
            confirmLabel: "Smazat",
            variant: "danger",
            onConfirm: () => remove.mutateAsync(floor.id),
          })
        }
        onMoveRow={move}
      />

      <FormModal
        open={modal.kind === "floor"}
        title={modal.kind === "floor" && modal.floor ? "Upravit velikost" : "Nová velikost"}
        onClose={close}
        submitLabel="Uložit"
        busy={create.isPending || update.isPending}
        canSubmit={!invalid}
        error={create.error || update.error}
        onSubmit={submit}
      >
        <Input
          label="Název"
          required
          placeholder="12 × 12 m"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <ResponsiveGrid columns={2}>
          <Input
            label="Šířka (m)"
            type="number"
            min={1}
            value={form.width}
            error={halfSize ? "Zadejte oba rozměry, nebo žádný." : undefined}
            onChange={(e) => setForm({ ...form, width: e.target.value })}
          />
          <Input
            label="Délka (m)"
            type="number"
            min={1}
            value={form.length}
            onChange={(e) => setForm({ ...form, length: e.target.value })}
          />
        </ResponsiveGrid>
        <Textarea
          label="Popis"
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          hint="Nepovinné, například na co se rozměr vztahuje."
        />
      </FormModal>
    </>
  );
}
